import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ClipboardList,
  Copy,
  ExternalLink,
  Eye,
  Loader,
  Monitor,
  Smartphone,
  Upload,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { PanelTour, type PanelTourStep } from "../components/ui/PanelTour";
import { formApi } from "../features/forms/api";
import {
  validateDefinition,
  type FeedbackForm,
} from "../features/forms/contract";
import { formPermissions } from "../features/forms/permissions";
import { QuestionsEditor } from "../features/forms/QuestionsEditor";
import { FormExperience } from "../features/forms/FormExperience";
import { FormSettings } from "../features/forms/FormSettings";
import { DeleteDraftButton } from "../features/forms/DeleteDraftButton";
import { Responses } from "../features/forms/Responses";
import { FormSharing } from "../features/forms/FormSharing";
type Tab = "questions" | "design" | "settings" | "share" | "responses";
const definitionKey = (form: FeedbackForm) =>
  JSON.stringify({
    title: form.title,
    description: form.description,
    confirmation: form.confirmation,
    questions: form.questions,
    design: form.design,
    event_id: form.event_id,
    after_event: form.after_event,
    award_points: form.award_points,
    reward_kind: form.reward_kind,
    reward_id: form.reward_id,
    status: form.status,
    closes_at: form.closes_at,
  });
export function FormDetailView() {
  const { id } = useParams(),
    navigate = useNavigate(),
    { identity, isLoading } = useAuth();
  const perms = formPermissions(identity, import.meta.env.DEV);
  const [form, setForm] = useState<FeedbackForm | null>(null),
    [tab, setTab] = useState<Tab>("questions"),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [saving, setSaving] = useState(false),
    [saveLabel, setSaveLabel] = useState("All changes saved"),
    [preview, setPreview] = useState(true),
    [mobile, setMobile] = useState(false),
    [publishOpen, setPublishOpen] = useState(false),
    [revision, setRevision] = useState(0);
  const saved = useRef(""),
    latest = useRef<FeedbackForm | null>(null),
    inFlight = useRef(false),
    restoreTab = useRef<Tab>("questions");
  latest.current = form;
  useDocumentTitle(form ? `${form.title} · Forms` : "Forms");
  useEffect(() => {
    let cancel = false;
    setForm(null);
    formApi
      .get(id!)
      .then((f) => {
        if (!cancel) {
          saved.current = definitionKey(f);
          let restored = f;
          try {
            const cached = sessionStorage.getItem("argus.form.draft." + f.id);
            if (cached) {
              const draft = JSON.parse(cached) as FeedbackForm;
              if (draft.revision === f.revision) {
                restored = { ...f, ...draft };
                setNotice("Your unfinished edits were restored.");
              }
            }
          } catch {}
          setForm(restored);
          setError("");
        }
      })
      .catch((e) => {
        if (!cancel) setError(e.message);
      });
    return () => {
      cancel = true;
    };
  }, [id, revision]);
  const save = useCallback(
    async (status?: FeedbackForm["status"]) => {
      const current = latest.current;
      if (!current || current.id !== id || inFlight.current || !perms.write)
        return false;
      const snapshot = { ...current, ...(status ? { status } : {}) };
      try {
        validateDefinition(snapshot);
      } catch (e) {
        setSaveLabel("Needs a little attention");
        setError(e instanceof Error ? e.message : "Check your questions.");
        return false;
      }
      if (!status && saved.current === definitionKey(snapshot)) return true;
      inFlight.current = true;
      setSaving(true);
      setSaveLabel("Saving…");
      setError("");
      try {
        const result = await formApi.save(snapshot);
        if (latest.current?.id !== snapshot.id) return true;
        saved.current = definitionKey(result);
        setForm((now) =>
          !now || definitionKey(now) === definitionKey(current)
            ? result
            : {
                ...now,
                revision: result.revision,
                response_count: result.response_count,
                status: result.status,
              },
        );
        setSaveLabel("All changes saved");
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not save.");
        setSaveLabel("Not saved · retry below");
        return false;
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [perms.write, id],
  );
  useEffect(() => {
    if (!form || !perms.write) return;
    try {
      if (definitionKey(form) === saved.current)
        sessionStorage.removeItem("argus.form.draft." + form.id);
      else
        sessionStorage.setItem(
          "argus.form.draft." + form.id,
          JSON.stringify(form),
        );
    } catch {}
    if (definitionKey(form) === saved.current) return;
    setSaveLabel("Unsaved changes");
    const timer = setTimeout(() => void save(), 1400);
    return () => clearTimeout(timer);
  }, [form, perms.write, save]);
  useEffect(() => {
    const prevent = (event: BeforeUnloadEvent) => {
      if (latest.current && definitionKey(latest.current) !== saved.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const key = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        void save();
      }
    };
    window.addEventListener("beforeunload", prevent);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("beforeunload", prevent);
      window.removeEventListener("keydown", key);
    };
  }, [save]);
  function change(update: Partial<FeedbackForm>) {
    setForm((f) => (f ? { ...f, ...update } : f));
    setNotice("");
  }
  async function duplicate() {
    if (!form) return;
    try {
      const copy = await formApi.create({
        ...form,
        title: `${form.title} (copy)`,
        status: "draft",
        closes_at: null,
      });
      navigate(`/forms/${copy.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not duplicate form.");
    }
  }
  async function share() {
    try {
      await navigator.clipboard.writeText(
        `https://craftdownunder.co/forms/${id}`,
      );
      setNotice("Hub link copied");
    } catch {
      setNotice(`Share this link: https://craftdownunder.co/forms/${id}`);
    }
  }
  const steps: PanelTourStep[] = [
    {
      id: "questions",
      tab: "questions",
      title: "Write like you are talking to a person",
      description:
        "Click any question to edit it. Choose an answer type, add context and mark only essential answers as required. Valid changes save automatically.",
      target: '[data-tour="forms-questions"]',
    },
    {
      id: "preview",
      tab: "questions",
      title: "Try the real experience",
      description:
        "This preview uses the exact Hub form. Answer questions, try conditional paths and switch to mobile. Preview submissions never save responses or award points.",
      target: '[data-tour="forms-preview"]',
      fallbackTarget: '[data-tour="forms-preview-toggle"]',
    },
    {
      id: "design",
      tab: "design",
      title: "Give it a little personality",
      description:
        "Choose a banner, accent and type style. Keep it familiar and readable; the Hub takes care of spacing and mobile layout.",
      target: '[data-tour="forms-design"]',
    },
    {
      id: "events",
      tab: "settings",
      title: "Event feedback, when you need it",
      description:
        "Forms work independently. Optionally connect an event, wait until it finishes and award a feedback point automatically. Each player can earn that point once per event.",
      target: '[data-tour="forms-settings"]',
    },
    {
      id: "rewards",
      tab: "settings",
      title: "Choose a thank-you, or keep it simple",
      description:
        "Completion reward defaults to none. Choose a Tesseract award or cosmetic Minecraft prefix, then select the reward. Event points require a linked event. Rewards are delivered once, with automatic retries, and lock after the first response.",
      target: '[data-tour="forms-rewards"]',
    },
    {
      id: "publish",
      tab: "questions",
      title: "A clear handoff to your players",
      description:
        "Publish after reviewing the form, then share its Hub link. Sign-in and a linked Minecraft account are always required. Delete draft removes an unpublished draft; published forms can be closed and reopened.",
      target: '[data-tour="forms-publish"]',
    },
    {
      id: "sharing",
      tab: "share",
      title: "Give your invitation a memorable link",
      description:
        "Copy the direct Hub link or create a CDU short link after publishing. Choose an optional name and private label, such as Discord announcement. Existing link names are protected automatically.",
      target: '[data-tour="forms-link-create"]',
      fallbackTarget: '[data-tour="forms-sharing"]',
    },
    {
      id: "link-management",
      tab: "share",
      title: "Keep track of every invitation",
      description:
        "Copy links, update their private labels and see aggregate visits here. Retire a link when it is no longer needed; it stops working immediately and its name stays reserved forever. Retiring a link does not delete responses or close the form.",
      target: '[data-tour="forms-link-manage"]',
    },
    {
      id: "responses",
      tab: "responses",
      title: "Turn answers into the next improvement",
      description:
        "Overview shows patterns; People keeps answers in context; Table compares responses side by side. Search, refresh, or export everything to a spreadsheet.",
      target: '[data-tour="forms-responses"]',
    },
  ];
  if (!perms.read) return <p>You do not have access to Forms.</p>;
  if (!form)
    return (
      <div className="glass-card p-8">{error || "Opening your form…"}</div>
    );
  const locked = form.response_count > 0,
    dirty = definitionKey(form) !== saved.current;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          className="btn-ghost text-xs"
          to="/forms"
          onClick={(e) => {
            if (dirty) {
              e.preventDefault();
              void save().then((ok) => {
                if (ok) navigate("/forms");
              });
            }
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          All forms
        </Link>
        <div className="flex items-center gap-3 text-xs text-glass-400">
          <span className="flex gap-1.5 items-center" role="status">
            {saving ? (
              <Loader className="w-3 h-3 animate-spin" />
            ) : (
              <Check className="w-3 h-3" />
            )}
            {perms.write ? saveLabel : "View only"}
          </span>
          <PanelTour
            tourId="forms-editor"
            title="Form builder"
            steps={steps.filter(
              (s) => perms.write || ["preview", "responses"].includes(s.id),
            )}
            enabled={!isLoading}
            onStart={() => {
              restoreTab.current = tab;
              setPreview(true);
            }}
            onNavigate={(s) => setTab(s.tab as Tab)}
            onEnd={() => setTab(restoreTab.current)}
          />
        </div>
      </div>
      <header className="glass-card p-5" data-tour="forms-publish">
        <div className="flex gap-4 justify-between flex-wrap">
          <div className="flex gap-3 items-start min-w-0 flex-1">
            <ClipboardList className="w-5 h-5 text-accent-300 mt-1" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold tracking-tight break-words">
                  {form.title || "Untitled form"}
                </h1>
                <span className="badge text-[10px] capitalize">
                  {form.status}
                </span>
              </div>
              <p className="text-xs text-glass-400 mt-1">
                {form.event_title
                  ? `Linked to ${form.event_title}`
                  : "An independent community form"}{" "}
                · {form.response_count} responses
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {perms.write && (
              <DeleteDraftButton
                form={form}
                disabled={saving}
                onDeleted={() => {
                  saved.current = definitionKey(form);
                  latest.current = null;
                  navigate("/forms");
                }}
              />
            )}
            {perms.write && (
              <button
                className="glass-button-secondary"
                onClick={() => void duplicate()}
                disabled={saving}
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
            )}
            {form.status === "published" && (
              <>
                <button
                  className="glass-button-secondary"
                  onClick={() => void share()}
                >
                  Copy Hub link
                </button>
                <a
                  href={`https://craftdownunder.co/forms/${form.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                  aria-label="Open form on Hub"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </>
            )}
            {perms.write &&
              (form.status === "published" ? (
                <button
                  className="glass-button-secondary"
                  disabled={saving || dirty}
                  onClick={() => void save("closed")}
                >
                  Close responses
                </button>
              ) : (
                <button
                  className="btn-primary"
                  disabled={saving}
                  onClick={() => setPublishOpen(true)}
                >
                  <Upload className="w-4 h-4" />
                  {form.status === "closed" ? "Reopen" : "Publish form"}
                </button>
              ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 justify-between mt-5">
          <div
            className="tabs max-sm:flex-wrap max-sm:overflow-visible"
            role="tablist"
            aria-label="Form workspace"
          >
            {(
              ["questions", "design", "settings", "share", "responses"] as const
            ).map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                className="tab capitalize"
                data-active={tab === t ? "true" : "false"}
                onClick={() => setTab(t)}
              >
                {t === "settings" ? "Settings & connections" : t}
              </button>
            ))}
          </div>
          {tab !== "responses" && tab !== "share" && (
            <button
              className="btn-ghost text-xs"
              data-tour="forms-preview-toggle"
              onClick={() => setPreview(!preview)}
            >
              <Eye className="w-4 h-4" />
              {preview ? "Hide preview" : "Show preview"}
            </button>
          )}
        </div>
      </header>
      {notice && (
        <p
          className="subcard p-3 text-xs text-accent-300 break-all"
          role="status"
        >
          {notice}
        </p>
      )}
      {error && (
        <div
          className="subcard p-4 flex flex-wrap items-center gap-3"
          role="alert"
        >
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button
            className="glass-button-secondary text-xs"
            disabled={saving}
            onClick={() => void save()}
          >
            Retry save
          </button>
          <button
            className="btn-ghost text-xs"
            disabled={saving}
            onClick={() => {
              try {
                sessionStorage.removeItem("argus.form.draft." + id);
              } catch {}
              setRevision((r) => r + 1);
              setNotice("Reloaded the latest saved version.");
            }}
          >
            Reload saved version
          </button>
        </div>
      )}
      {publishOpen && (
        <section
          className="glass-card p-5 space-y-4"
          aria-label="Publish review"
        >
          <h2 className="font-medium">Ready to hear from your community?</h2>
          <div className="grid sm:grid-cols-3 gap-3 text-xs">
            <div className="subcard p-3">
              <strong>
                {form.questions.filter((q) => q.type !== "section").length}{" "}
                questions
              </strong>
              <p className="text-glass-400 mt-1">
                One response per linked player.
              </p>
            </div>
            <div className="subcard p-3">
              <strong>
                {form.after_event
                  ? "Opens after the event"
                  : "Opens immediately"}
              </strong>
              <p className="text-glass-400 mt-1">
                {form.after_event
                  ? "The event must be marked completed."
                  : "Share the Hub link after publishing."}
              </p>
            </div>
            <div className="subcard p-3">
              <strong>{form.reward_label || "No reward"}</strong>
              <p className="text-glass-400 mt-1">
                {form.closes_at
                  ? `Closes ${new Date(form.closes_at).toLocaleString()}`
                  : "Responses stay open until you close them."}
              </p>
            </div>
          </div>
          <p className="text-xs text-glass-400">
            Questions and event settings lock when the first response arrives.
            You can still update the introduction, styling and thank-you
            message.
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="glass-button-secondary"
              onClick={() => setPublishOpen(false)}
            >
              Keep editing
            </button>
            <button
              className="btn-primary"
              disabled={saving}
              onClick={() =>
                void save("published").then((ok) => {
                  if (ok) {
                    setPublishOpen(false);
                    setNotice(
                      "Published. Copy the Hub link above to invite your community.",
                    );
                  }
                })
              }
            >
              Publish and accept responses
            </button>
          </div>
        </section>
      )}
      {tab === "responses" ? (
        <Responses form={form} />
      ) : tab === "share" ? (
        <FormSharing key={form.id} form={form} canWrite={perms.write} />
      ) : (
        <div
          className={`grid gap-5 items-start ${preview ? "xl:grid-cols-[minmax(350px,1fr)_minmax(380px,1fr)]" : ""}`}
        >
          <fieldset disabled={!perms.write} className="min-w-0 space-y-4">
            {tab === "questions" && (
              <>
                <section className="glass-card p-5 space-y-4">
                  <label className="block text-xs space-y-2">
                    <span className="text-glass-400">Form title</span>
                    <input
                      className="glass-input w-full text-lg font-medium"
                      maxLength={200}
                      value={form.title}
                      onChange={(e) => change({ title: e.target.value })}
                    />
                  </label>
                  <label className="block text-xs space-y-2">
                    <span className="text-glass-400">A warm introduction</span>
                    <textarea
                      className="glass-input w-full"
                      rows={3}
                      maxLength={5000}
                      value={form.description}
                      onChange={(e) => change({ description: e.target.value })}
                      placeholder="Why are you asking? What will their answers help you do?"
                    />
                  </label>
                </section>
                <QuestionsEditor
                  questions={form.questions}
                  onChange={(questions) => change({ questions })}
                  locked={locked}
                />
              </>
            )}
            {tab === "design" && (
              <section
                className="glass-card p-5 space-y-6"
                data-tour="forms-design"
              >
                <div>
                  <h2 className="font-medium text-sm">
                    A little personality goes a long way.
                  </h2>
                  <p className="text-xs text-glass-400 mt-2">
                    Your choices carry through to the real Hub form.
                  </p>
                </div>
                <label className="block text-xs space-y-2">
                  <span className="text-glass-400">Header style</span>
                  <select
                    className="glass-select w-full"
                    value={form.design.banner}
                    onChange={(e) =>
                      change({
                        design: {
                          ...form.design,
                          banner: e.target
                            .value as FeedbackForm["design"]["banner"],
                        },
                      })
                    }
                  >
                    <option value="landscape">
                      The world we share · landscape
                    </option>
                    <option value="festival">
                      A splash of colour · celebration
                    </option>
                    <option value="minimal">Room to think · minimal</option>
                  </select>
                </label>
                <div>
                  <p className="text-xs text-glass-400 mb-3">Accent colour</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "#9fcda8",
                      "#a2cde3",
                      "#bdb3df",
                      "#e7bd8a",
                      "#e0a6ac",
                      "#d7ca87",
                    ].map((accent) => (
                      <button
                        key={accent}
                        type="button"
                        aria-label={`Use accent ${accent}`}
                        aria-pressed={form.design.accent === accent}
                        className="w-10 h-10 rounded-full border-2"
                        style={{
                          background: accent,
                          borderColor:
                            form.design.accent === accent
                              ? "var(--text-primary)"
                              : "transparent",
                        }}
                        onClick={() =>
                          change({ design: { ...form.design, accent } })
                        }
                      />
                    ))}
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="color"
                        aria-label="Custom accent colour"
                        className="w-10 h-10 rounded cursor-pointer"
                        value={form.design.accent}
                        onChange={(e) =>
                          change({
                            design: { ...form.design, accent: e.target.value },
                          })
                        }
                      />
                      Custom
                    </label>
                  </div>
                </div>
                <label className="block text-xs space-y-2">
                  <span className="text-glass-400">Typography</span>
                  <select
                    className="glass-select w-full"
                    value={form.design.font}
                    onChange={(e) =>
                      change({
                        design: {
                          ...form.design,
                          font: e.target.value as "sans" | "serif",
                        },
                      })
                    }
                  >
                    <option value="sans">Clear & familiar</option>
                    <option value="serif">Warm & editorial</option>
                  </select>
                </label>
              </section>
            )}
            {tab === "settings" && (
              <FormSettings
                form={form}
                onChange={change}
                canEvents={perms.events}
                canRewards={perms.rewards}
                locked={locked}
              />
            )}
          </fieldset>
          {preview && (
            <aside
              data-tour="forms-preview"
              className="min-w-0 xl:sticky xl:top-4"
            >
              <div className="flex justify-between items-center mb-3 text-xs text-glass-400">
                <span>Live Hub preview</span>
                <div className="flex gap-1">
                  <button
                    className="btn-ghost"
                    aria-label="Desktop preview"
                    aria-pressed={!mobile}
                    onClick={() => setMobile(false)}
                  >
                    <Monitor className="w-4 h-4" />
                  </button>
                  <button
                    className="btn-ghost"
                    aria-label="Mobile preview"
                    aria-pressed={mobile}
                    onClick={() => setMobile(true)}
                  >
                    <Smartphone className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div
                className={`mx-auto max-h-[78vh] overflow-y-auto rounded-2xl ${mobile ? "max-w-[390px]" : ""}`}
              >
                <FormExperience key={form.id} form={form} preview />
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
